/**
 * Diálogo para corregir el número de GPS (gps.gps) de una venta general ya
 * registrada. Solo cambia ese dato: valor, pagos y distribución en cuotas
 * quedan intactos. Lo comparten Mantenimiento de Ventas Generales (permiso
 * ares9620 + modificar) y el expediente del financiamiento (expediente +
 * modificar); el backend (/api/ventageneral_editargps) valida duplicados,
 * largo y deja el cambio en la bitácora.
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };

export default function EditarGpsDialog({ venta, onClose, onSaved }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [valor, setValor] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setValor(venta?.gps || "");
  }, [venta]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_editargps", {
        id: venta.id,
        gps: valor.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Número de GPS actualizado", color: "success" });
        if (onSaved) onSaved(data.gps ?? valor.trim());
        onClose();
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar el GPS", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={!!venta}
      onClose={guardando ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
        Cambiar número de GPS
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: "0.88rem", mb: 2 }}>
          Venta <b>#{venta?.id}</b>
          {venta?.descripcion ? ` · ${venta.descripcion}` : ""}. Solo se corrige el número; el
          valor, los pagos y las cuotas no cambian.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="GPS #"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !guardando && guardar()}
          helperText={venta?.gps ? `Actual: ${venta.gps}` : "La venta no tiene número de GPS"}
          sx={fpInputSx}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={guardando}
          sx={{ ...btnBase, color: FP.color.textSecondary }}
        >
          Cancelar
        </Button>
        <Button
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={guardando || valor.trim() === (venta?.gps || "").trim()}
          sx={{
            ...btnBase,
            bgcolor: FP.color.navy,
            color: "#FFFFFF",
            "&:hover": { bgcolor: FP.color.navySoft, color: "#FFFFFF" },
            "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditarGpsDialog.propTypes = {
  venta: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gps: PropTypes.string,
    descripcion: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

EditarGpsDialog.defaultProps = {
  venta: null,
  onSaved: null,
};
